/**
 * The deep N-way comparison: what rows exist, what each one says, and where a difference
 * may **not** be claimed.
 *
 * ## Why this file exists, and what it replaces
 *
 * `compare.js` renders the 1-vs-many table, and it renders **two** fields: Rating and
 * Reviews. Meanwhile the popup's 1-vs-1 view compares rating, review count, rating
 * distribution, opening hours, Popular Times, photos, service options, Q&A and attributes.
 *
 * So the companion was the *wide but shallow* view and the popup was the *deep but
 * one-at-a-time* view. Neither could do what an owner actually wants: the depth of 1-vs-1
 * applied across every business at once.
 *
 * This file builds that row model. The data is already in every export — `hours.parsed`,
 * `reputation.distribution`, `popularTimes`, `photos`, `reviews.insights`, `qa.summary`,
 * `completeness` — so this is a reading job, not an extraction one.
 *
 * ## The one rule that matters more than any row
 *
 * **A difference is reported only when both sides were actually read.** Stated in the
 * codebase's own words: *one gap is not two.*
 *
 * The popup learned this the hard way. Its busiest-hour row compared peak hours with
 * `a === b`, so when rother3 could read *neither* side, `null === null` was `true` and the
 * row rendered a green tick — agreement with a competitor about a number neither published.
 * A false difference is worse than a missing one, because the reader acts on it.
 *
 * N businesses make this worse, not better: with 20 columns, a row that silently compares
 * `null` to `null` claims 190 pairwise agreements. So **every** cell here carries its own
 * readability, and a verdict is computed only from cells that are readable.
 */
import { auditFields, categoryNames } from './audit-shape.js';

/**
 * What a cell can be.
 *
 * `unknown` and `notPublished` are kept apart deliberately, and the distinction is the
 * project's usual one (`popularTimesState`, `photos.countState`): nobody can add crowd data
 * to a Business Profile, so reporting "rother3 could not read this" for a listing that
 * publishes none sends the owner hunting a fault that does not exist.
 */
export const CELL = {
  /** A value rother3 read. */
  ok: 'ok',
  /** The slot was never present — an older export, or a v1 file. */
  unknown: 'unknown',
  /** Maps positively says this business has none. A statement about the business. */
  notPublished: 'not-published',
  /** rother3 was there and did not read it. Our defect, and the owner's problem. */
  unread: 'unread',
};

/** Build one cell. Every reader in this file goes through here, so none can skip the state. */
function cell(value, state = CELL.ok) {
  return { value, state, readable: state === CELL.ok };
}

/** A null/absent value with the correct state for *why* it is absent. */
function absent(state) {
  return { value: null, state, readable: false };
}

// ── Field readers ──────────────────────────────────────────────────────────
//
// Each returns a `cell` for one business. They never return a bare value, because a bare
// `null` is exactly what produced the green-tick defect: it cannot be told apart from a
// value that happens to be null.

/** Rating, 0-5, or unread. */
function readRating(f) {
  const v = parseFloat(f.rating);
  return Number.isFinite(v) ? cell(v) : absent(CELL.unknown);
}

/** Review count, or unknown. Never 0 for "unread" — that would be a false claim. */
function readReviewCount(f) {
  const v = parseFloat(f.reviewCount);
  return Number.isFinite(v) ? cell(Math.round(v)) : absent(CELL.unknown);
}

/** One star bucket of the distribution, plus its share of that business's own total. */
function readStar(f, star) {
  const dist = f.distribution;
  if (!Array.isArray(dist)) {return absent(CELL.unknown);}
  const entry = dist.find((d) => Number(d?.star) === star);
  if (!entry || typeof entry.count !== 'number') {
    return absent(CELL.unread);
  }
  const total = dist.reduce((sum, d) => sum + (typeof d?.count === 'number' ? d.count : 0), 0);
  return cell({ count: entry.count, share: total > 0 ? (entry.count / total) * 100 : 0 });
}

/** How many of the seven days a listing publishes hours for. */
function readHoursDays(f) {
  const parsed = f.hoursMeta?.parsed;
  if (!Array.isArray(parsed)) {
    // `hours` is a plain string on a v1 export and an object on v2, so a flat record with
    // a string is *known* to be an old file, not a read that failed.
    return typeof f.hours === 'string' && f.hours.length > 0
      ? cell({ count: 1, text: 'one schedule' })
      : absent(CELL.unknown);
  }
  const rows = parsed.filter((r) => r && typeof r.day === 'string' && r.day.length > 0);
  return cell({ count: rows.length, rows });
}

/** One weekday's hours, as the string Maps rendered. */
function readHoursDay(f, dayName) {
  const parsed = f.hoursMeta?.parsed;
  if (!Array.isArray(parsed)) {return absent(CELL.unknown);}
  const row = parsed.find((r) => r?.day === dayName);
  if (row === undefined) {
    // The day is simply not in that business's schedule. That is a fact about the listing,
    // and for a comparison it is the *interesting* one — a competitor closed on Tuesdays
    // is a finding, not a gap in our read.
    return absent(CELL.notPublished);
  }
  return cell(typeof row.time === 'string' ? row.time : '');
}

/** Total photos, or the reason there are none. */
function readPhotoTotal(f) {
  const pt = f.photos;
  if (!pt || typeof pt !== 'object') {return absent(CELL.unknown);}
  if (typeof pt.total === 'number') {return cell(pt.total);}
  return absent(pt.countState === 'not-published' ? CELL.notPublished : CELL.unread);
}

/** Logo / cover presence as a pass-fail. */
function readPhotoFlag(f, key) {
  const pt = f.photos;
  if (!pt || typeof pt !== 'object' || !(key in pt)) {return absent(CELL.unknown);}
  return cell(pt[key] === true);
}

/** Peak hours, or the reason there is no chart. */
function readPeakHours(f) {
  const pt = f.popularTimes;
  const state = f.popularTimesState;
  if (!pt || typeof pt !== 'object') {
    if (state === 'not-published') {return absent(CELL.notPublished);}
    if (state === 'unread') {return absent(CELL.unread);}
    return absent(CELL.unknown);
  }
  const peaks = Array.isArray(pt.peakHours)
    ? pt.peakHours.filter((p) => p && typeof p.hour === 'number').map((p) => p.hour)
    : [];
  return cell({ hours: peaks, days: Array.isArray(pt.busiestDays) ? pt.busiestDays : [] });
}

/** Busiest days, or the reason there is no chart. */
function readBusiestDays(f) {
  const pt = f.popularTimes;
  if (!pt || typeof pt !== 'object') {
    if (f.popularTimesState === 'not-published') {return absent(CELL.notPublished);}
    if (f.popularTimesState === 'unread') {return absent(CELL.unread);}
    return absent(CELL.unknown);
  }
  const days = Array.isArray(pt.busiestDays) ? pt.busiestDays.filter((d) => typeof d === 'string') : [];
  return cell(days);
}

/** How many days of the week the crowd chart covered. */
function readCrowdCoverage(f) {
  const pt = f.popularTimes;
  if (!pt || !Array.isArray(pt.days)) {return absent(CELL.unknown);}
  return cell({ read: pt.days.length, of: 7 });
}

/** Owner reply rate, as a percentage. Derived from the **sample**, so it is flagged. */
function readReplyRate(f) {
  const meta = f.reviewsMeta;
  const rate = meta?.insights?.responseRate?.rate;
  if (typeof rate !== 'number' || !Number.isFinite(rate)) {return absent(CELL.unknown);}
  return cell({ percent: rate * 100, display: meta.insights.responseRate.display ?? null });
}

/**
 * Total Q&A and how many the owner answered.
 *
 * ## The key is `qa`, not `qaMeta` — and that was a real bug
 *
 * The first version of this read `f.qaMeta?.summary` and `f.qaItems`. **`flattenV2` creates
 * neither.** It copies `qa.items` to `qaItems` and leaves the whole wrapper on the original
 * `qa` key, so `qaMeta` was always `undefined` and every Q&A row reported zero businesses —
 * rendered as "Not in any export: Questions asked" on a listing whose export plainly carried
 * the section. A reader could not tell a missing section from a broken one, which is the exact
 * ambiguity this project treats as worst.
 *
 * The lesson generalises past this row: **a reader must use the key the normalizer actually
 * produces.** The `*Meta` convention exists only for the four sections whose flat name
 * *collides* with v1 — `reviews`, `hours`, `attributes`, `categories`. `qa` and `completeness`
 * do not collide, so they keep their own names and get no `*Meta` twin. Both shapes are
 * accepted here so a v1 file (flat, no wrapper) and a v2 file (wrapped) both read.
 */
function readQa(f) {
  // v2: the wrapper survives on `qa` itself. v1: a flat array on `qaItems`.
  const summary = f.qa?.summary ?? f.qaItems?.summary;
  const items = Array.isArray(f.qa?.items) ? f.qa.items : f.qaItems;

  if (typeof summary?.totalQuestions === 'number') {
    return cell({
      total: summary.totalQuestions,
      answeredRate: typeof summary.ownerAnswerRate === 'number' ? summary.ownerAnswerRate * 100 : null,
    });
  }
  if (Array.isArray(items)) {return cell({ total: items.length, answeredRate: null });}
  return absent(CELL.unknown);
}

/**
 * Count of amenity groups.
 *
 * `attributesMeta` **is** a real key — it is one of the four colliding sections — so this row
 * was right, and it is here beside `readQa` so the difference is visible. `attributes` collides
 * with v1 (an array in both, which is why the wrapper is preserved), so the normalizer writes
 * both `attributes` and `attributesMeta`.
 */
function readAttributes(f) {
  const items = f.attributesMeta?.items ?? (Array.isArray(f.attributes) ? f.attributes : null);
  if (!Array.isArray(items)) {return absent(CELL.unknown);}
  return cell({ groups: items.length });
}

/** Primary and secondary category counts — the two numbers that are actually comparable. */
function readCategories(f) {
  const cat = f.categories;
  if (!Array.isArray(cat)) {return absent(CELL.unknown);}
  const primary = cat.filter((c) => c?.isPrimary === true);
  const secondary = cat.filter((c) => c?.isPrimary !== true);
  return cell({ primary: primary.length, secondary: secondary.length, names: categoryNames(cat) });
}

/**
 * Missing checklist items, as a count and the names.
 *
 * **`completeness`, not `completenessMeta`** — the same bug as `readQa`. `completeness` does
 * not collide with a v1 key, so the normalizer leaves it in place and creates no `*Meta` twin.
 * Reading `completenessMeta` meant this row reported "Not in any export" on every listing
 * whose export carried the checklist.
 */
function readMissing(f) {
  const items = f.completeness?.missingItems;
  if (!Array.isArray(items)) {return absent(CELL.unknown);}
  return cell({ count: items.length, names: items.filter((n) => typeof n === 'string') });
}

// ── The row model ──────────────────────────────────────────────────────────

/**
 * A row: one field, N cells, and a verdict computed only from readable ones.
 *
 * `better` is **'lower' | 'higher' | 'none'** and the name is deliberately unhelpful —
 * "better" is a judgement this file is not entitled to make for every field. Photos: more
 * is better for a listing. Review count: more is better. Busiest hours: not comparable at
 * all. The caller renders the tone; this file only knows the direction.
 *
 * @typedef {object} CompareRow
 * @property {string} key
 * @property {string} label
 * @property {string} [hint]  What the value means, for a column header or a tooltip.
 * @property {'lower'|'higher'|'none'} better
 * @property {'number'|'percent'|'hours'|'days'|'list'|'flag'|'text'|'object'} format
 * @property {CompareCell[]} cells
 * @property {number} readableCount  How many businesses this row could say anything about.
 * @property {WidestGap|null} widest  `null` below two readable cells — see `widestGap`.
 */

/**
 * @typedef {object} CompareCell
 * @property {string|null} placeId
 * @property {string} businessName
 * @property {unknown} value
 * @property {string} state       One of `CELL`. Never a bare null, which is the whole point.
 * @property {boolean} readable
 */

/**
 * @typedef {object} WidestGap
 * @property {string|null} placeId
 * @property {string} businessName
 * @property {number} delta       Signed: `other - anchor`, matching `computeDelta`.
 */

/**
 * Build one row from a reader.
 *
 * @param {object[]} fields - every business, anchor first
 * @param {(f: object) => {value: unknown, state: string, readable: boolean}} reader
 */
function buildRow(key, label, hint, better, format, fields, reader) {
  const cells = fields.map((f) => ({
    placeId: typeof f.placeId === 'string' ? f.placeId : null,
    businessName: typeof f.businessName === 'string' ? f.businessName : 'Unknown',
    ...reader(f),
  }));

  const readable = cells.filter((c) => c.readable);
  return {
    key,
    label,
    hint,
    better,
    format,
    cells,
    readableCount: readable.length,
    widest: widestGap(cells, better, format),
  };
}

/**
 * The largest gap between the anchor and any readable competitor.
 *
 * Returns `null` below two readable cells, and that guard is the whole point of this
 * function: `widest` is a claim *about a relationship between businesses*, and with one
 * readable business there is no relationship to describe.
 */
function widestGap(cells, better, format) {
  if (better === 'none' || !['number', 'percent'].includes(format)) {return null;}
  const anchor = cells[0];
  if (!anchor?.readable || typeof anchor.value !== 'number') {return null;}

  let widest = null;
  for (const c of cells.slice(1)) {
    if (!c.readable || typeof c.value !== 'number') {continue;}
    const delta = c.value - anchor.value;
    if (widest === null || Math.abs(delta) > Math.abs(widest.delta)) {
      widest = { placeId: c.placeId, businessName: c.businessName, delta };
    }
  }
  return widest;
}

/** Weekday order, for the hours rows. Monday-first: a business week reads that way. */
const DAYS = [
  'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday',
];

/**
 * The Indonesian and English day names the export carries, in the order Maps wrote them.
 *
 * `hours.parsed[].day` is **Maps' own wording**, which is Indonesian on an Indonesian page
 * and English on an English one — the reader is deliberately verbatim (`hours-read.ts`).
 * So the two lists are the same seven days in the same order, and a row is looked up by
 * index rather than by name, which is what makes a mixed-language comparison work at all.
 */
const ID_DAYS = [
  'Senin', 'Selasa', 'Rabu', 'Kamis', 'Jumat', 'Sabtu', 'Minggu',
];

/**
 * Every comparison row, grouped into sections.
 *
 * Section order follows the popup's 1-vs-1 view, so a reader who knows one recognises the
 * other — the popup's order is itself a considered decision and duplicating it here would
 * invite the two to drift.
 */
export function buildComparisonSections(fields) {
  const row = (key, label, better, format, reader, hint) =>
    buildRow(key, label, hint, better, format, fields, reader);

  return [
    {
      id: 'reputation',
      title: 'Reputation',
      note: 'Figures Maps publishes for every review, not the sample rother3 read.',
      rows: [
        row('rating', 'Rating', 'higher', 'number', readRating, 'Average star rating, 0–5.'),
        row('reviews', 'Reviews', 'higher', 'number', readReviewCount, 'Total reviews on the listing.'),
        row('stars5', '5-star reviews', 'higher', 'object', (f) => readStar(f, 5), 'Count and share of all reviews.'),
        row('stars4', '4-star reviews', 'none', 'object', (f) => readStar(f, 4)),
        row('stars1', '1-star reviews', 'lower', 'object', (f) => readStar(f, 1), 'Fewer is better here, unlike the rows above.'),
      ],
    },
    {
      id: 'hours',
      title: 'Opening hours',
      note: 'As Maps rendered them. Days a business does not open are a fact, not a gap in our read.',
      rows: [
        row('hours-days', 'Days with published hours', 'higher', 'number', readHoursDays),
        ...DAYS.map((dayName, i) =>
          buildRow(
            `hours-${i}`,
            dayName,
            undefined,
            'none',
            'hours',
            fields,
            (f) => {
              const parsed = f.hoursMeta?.parsed;
              // Match by **position**, not by name: `hours.parsed[].day` is Maps' own
              // wording, so an Indonesian file says "Selasa" where an English one says
              // "Tuesday", and a name lookup would silently blank every row on one of them.
              const rowAt = Array.isArray(parsed) ? parsed[i] : undefined;
              if (rowAt === undefined) {
                return typeof f.hours === 'string' && f.hours.length > 0
                  ? { value: null, state: CELL.unknown, readable: false }
                  : { value: null, state: CELL.unknown, readable: false };
              }
              return {
                value: typeof rowAt.time === 'string' ? rowAt.time : '',
                state: CELL.ok,
                readable: true,
              };
            }
          )
        ),
      ],
    },
    {
      id: 'crowd',
      title: 'Popular times',
      note: 'A listing that publishes no crowd data is not a gap in our read — nobody can add it to a Business Profile.',
      rows: [
        row('peak-hours', 'Busiest hours', 'none', 'list', readPeakHours),
        row('busiest-days', 'Busiest days', 'none', 'list', readBusiestDays),
        row('crowd-coverage', 'Days of crowd data', 'higher', 'object', readCrowdCoverage, 'Out of seven. Fewer than seven means an incomplete week.'),
      ],
    },
    {
      id: 'photos',
      title: 'Photos',
      rows: [
        row('photo-total', 'Total photos', 'higher', 'number', readPhotoTotal),
        row('photo-logo', 'Has a logo', 'higher', 'flag', (f) => readPhotoFlag(f, 'hasLogo')),
        row('photo-cover', 'Has a cover photo', 'higher', 'flag', (f) => readPhotoFlag(f, 'hasCover')),
      ],
    },
    {
      id: 'reviews',
      title: 'Review response',
      note: 'Computed from the reviews rother3 read, which is a small sample of the total. Read the reply rate beside the "Reviews" row above.',
      rows: [
        row('reply-rate', 'Reviews answered', 'higher', 'object', readReplyRate, 'Share of the sampled reviews the owner replied to.'),
        row('qa-total', 'Questions asked', 'none', 'object', readQa),
        row('qa-answered', 'Questions answered', 'higher', 'object', (f) => {
          const q = readQa(f);
          return q.readable ? cell({ percent: q.value.answeredRate }) : q;
        }),
        row('attributes', 'Amenity groups', 'higher', 'object', readAttributes),
      ],
    },
    {
      id: 'categories',
      title: 'Categories',
      rows: [
        row('cat-primary', 'Primary category', 'none', 'object', readCategories),
        row('cat-secondary', 'Secondary categories', 'higher', 'object', (f) => {
          const c = readCategories(f);
          return c.readable ? cell({ count: c.value.secondary, names: c.value.names }) : c;
        }),
      ],
    },
    {
      id: 'completeness',
      title: 'Listing completeness',
      note: 'What Maps is missing on each listing. Fewer gaps is better.',
      rows: [
        row('missing-items', 'Missing checklist items', 'lower', 'object', readMissing, 'Count and the names, so the number can be checked.'),
      ],
    },
  ];
}

/** The ID_DAYS list, exported so a locale-aware caller can label the hours rows. */
export const DAY_NAMES_BY_INDEX = { en: DAYS, id: ID_DAYS };
